import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as d3 from 'd3';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import StaffFansStatsCard from '../components/StaffFansStatsCard';
import Icon from '../components/Icon';
import SchoolPicker, { inlineSelectStyle } from '../components/SchoolPicker';
import apiService from '../services/api';
import { getHcRevenue, getSchoolRevenue } from '../utils/transactionRevenue';
// The funnel vocabulary, not the raw per-vendor status: `pending` was never in
// STATUS_FILTER_OPTIONS despite holding most rows, and `auth` vs `pending` only
// ever said which vendor reported the sale.
import { EXCLUDE_DEAD, FUNNEL_FILTER_OPTIONS, matchesFunnelStatus } from '../utils/funnel';
import {
  SOURCE_FILTER_OPTIONS,
  SOURCE_LABELS,
  getTransactionSource,
} from '../utils/transactionSource';
import oliveLogo from '../assets/images/olive-logo.png';
import wildfireLogo from '../assets/images/wildfire-logo.png';
import '../styles/StaffPages.css';

const SuperuserTransactionManagement = () => {
  const [transactions, setTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]); // Store all transactions for filtering
  const [stats, setStats] = useState({
    totalTransactions: 0,
    hcRevenue: 0,
    schoolRevenue: 0,
    loading: true
  });
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');
  // Failed, refunded and voided transactions carry a vendor commission but no
  // sale that was kept, so they are out of every total unless selected.
  const [selectedStatus, setSelectedStatus] = useState(EXCLUDE_DEAD);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [choseAll, setChoseAll] = useState(false);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Chart refs
  const lineChartRef = useRef(null);
  // The volume chart was hard-coded to 600px, so a crowded x-axis squeezed the ticks
  // together no matter how much room the card had. Measure the card instead.
  const lineChartCardRef = useRef(null);
  const [lineChartWidth, setLineChartWidth] = useState(0);
  // Past this many points, sharing a row with the revenue chart is too tight; the card
  // takes the full width and the chart grows with it.
  const [lineChartPointCount, setLineChartPointCount] = useState(0);

  useEffect(() => {
    const node = lineChartCardRef.current;
    if (!node) return undefined;
    const measure = () => setLineChartWidth(node.clientWidth || 0);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
    // Keyed on `loading`, not []: the component early-returns a spinner while loading,
    // so on mount the card does not exist yet and the ref is null. With [] deps this
    // effect would bail once and never measure, leaving the chart at its 600px fallback.
  }, [loading]);
  const revenueChartRef = useRef(null);

  const fetchData = async () => {
    try {
      const currentUser = await apiService.fetchCurrentUser();

      if (!currentUser?.is_superuser) {
        navigate('/staff/dashboard/');
        return;
      }

      const response = await apiService.getAllTransactionsSuperuser();
      const transactionData = response.transactions || [];
      setAllTransactions(transactionData);
      setTransactions(transactionData);

      // Extract unique schools from transactions with images
      const uniqueSchools = [...new Set(transactionData
        .filter(t => t.school && t.school.name)
        .map(t => JSON.stringify({
          id: t.school.id,
          name: t.school.name,
          image: t.school.image || null
        })))]
        .map(s => JSON.parse(s))
        .sort((a, b) => a.name.localeCompare(b.name));
      setSchools(uniqueSchools);

      // Calculate stats
      const totalTransactions = transactionData.length;

      // HC Revenue is commission * (1 - school split)
      const hcRevenue = transactionData.reduce((sum, transaction) => {
        return sum + getHcRevenue(transaction);
      }, 0);

      const schoolRevenue = transactionData.reduce((sum, transaction) => {
        return sum + getSchoolRevenue(transaction);
      }, 0);

      setStats({
        totalTransactions,
        hcRevenue,
        schoolRevenue,
        loading: false
      });
    } catch (error) {
      console.error('Failed to fetch transaction data:', error);
      setTransactions([]);
      setStats(prev => ({ ...prev, loading: false }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [navigate]);

  // Generate month options from January 2026 onwards
  const generateMonthOptions = () => {
    const options = [{ value: 'all', label: 'All Dates' }];
    const startDate = new Date(2026, 0); // January 2026
    const currentDate = new Date();
    // Only go up to current month
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

    const current = new Date(startDate);
    while (current <= endDate) {
      const monthYear = `${current.getFullYear()}-${(current.getMonth() + 1).toString().padStart(2, '0')}`;
      const label = current.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value: monthYear, label });
      current.setMonth(current.getMonth() + 1);
    }

    return options;
  };

  // Filter transactions by selected month, source, and school
  useEffect(() => {
    if (!choseAll && !selectedSchool) {
      setTransactions([]);
      setStats({
        totalTransactions: 0,
        hcRevenue: 0,
        schoolRevenue: 0,
        loading: false
      });
      return;
    }

    let filtered = [...allTransactions];

    // Filter by month
    if (selectedMonth !== 'all') {
      filtered = filtered.filter(transaction => {
        const transDate = new Date(transaction.created_at || transaction.auth_at || transaction.captured_at);
        const transMonthYear = `${transDate.getFullYear()}-${(transDate.getMonth() + 1).toString().padStart(2, '0')}`;
        return transMonthYear === selectedMonth;
      });
    }

    // Filter by status
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(transaction =>
        matchesFunnelStatus(transaction, selectedStatus)
      );
    }

    // Filter by source
    if (selectedSource !== 'all') {
      filtered = filtered.filter(transaction => {
        const source = getTransactionSource(transaction);
        return source === selectedSource;
      });
    }

    // Filter by school
    if (selectedSchool) {
      filtered = filtered.filter(transaction => {
        return transaction.school && transaction.school.id === selectedSchool.id;
      });
    }

    setTransactions(filtered);

    // Recalculate stats for filtered transactions
    const filteredTransactions = filtered;

    const totalTransactions = filteredTransactions.length;

    // HC Revenue is commission * (1 - school split)
    const hcRevenue = filteredTransactions.reduce((sum, transaction) => {
      return sum + getHcRevenue(transaction);
    }, 0);

    const schoolRevenue = filteredTransactions.reduce((sum, transaction) => {
      return sum + getSchoolRevenue(transaction);
    }, 0);

    setStats({
      totalTransactions,
      hcRevenue,
      schoolRevenue,
      loading: false
    });
  }, [selectedMonth, selectedSource, selectedSchool, selectedStatus, allTransactions, choseAll]);

  // Render D3 chart
  useEffect(() => {
    // Clear previous chart and tooltips
    d3.select(lineChartRef.current).selectAll("*").remove();
    d3.selectAll(".d3-tooltip").remove();

    // Line Chart - Transaction Volume Over Time
    const renderLineChart = () => {
      // Process data based on selected filter
      let processedData;
      let xTickFormat;

      if (selectedMonth === 'all') {
        // Group by month when showing all dates
        const validTransactions = transactions.filter(t => {
          const dateStr = t.created_at || t.auth_at || t.captured_at;
          return dateStr && !isNaN(Date.parse(dateStr));
        });

        if (validTransactions.length > 0) {
          processedData = d3.rollups(
            validTransactions,
            v => v.length,
            d => d3.timeMonth(new Date(d.created_at || d.auth_at || d.captured_at))
          ).map(([date, count]) => ({ date, count }))
            .sort((a, b) => a.date - b.date);
        } else {
          processedData = [];
        }

        xTickFormat = d3.timeFormat("%b"); // Show month abbreviation
      } else {
        // Group by day when a specific month is selected
        const transactionsByDay = d3.rollups(
          transactions,
          v => v.length,
          d => d3.timeDay(new Date(d.created_at || d.auth_at || d.captured_at))
        );

        // Create a map for quick lookup
        const dayMap = new Map(transactionsByDay.map(([date, count]) => [date.getTime(), count]));

        // Fill in all days for the selected month
        if (selectedMonth && selectedMonth !== 'all') {
          const [year, month] = selectedMonth.split('-').map(Number);
          const startDate = new Date(year, month - 1, 1);
          const endDate = new Date(year, month, 0);

          processedData = [];
          const currentDate = new Date(startDate);

          while (currentDate <= endDate) {
            const dayKey = d3.timeDay(new Date(currentDate));
            processedData.push({
              date: new Date(currentDate),
              count: dayMap.get(dayKey.getTime()) || 0
            });
            currentDate.setDate(currentDate.getDate() + 1);
          }
        } else {
          processedData = transactionsByDay
            .map(([date, count]) => ({ date, count }))
            .sort((a, b) => a.date - b.date);
        }

        xTickFormat = d3.timeFormat("%d"); // Show day number
      }

      const dailyData = processedData;
      setLineChartPointCount(dailyData.length);

      // Check if we have any actual transactions to display
      const hasTransactions = dailyData.some(d => d.count > 0);
      if (dailyData.length === 0 || !hasTransactions) {
        d3.select(lineChartRef.current)
          .append("div")
          .style("display", "flex")
          .style("align-items", "center")
          .style("justify-content", "center")
          .style("height", "260px")
          .style("width", "100%")
          .style("color", "#9CA3AF")
          .style("font-family", "Baikal Book")
          .style("font-size", "14px")
          .text("No transactions with the current filter selection");
        return;
      }

      const margin = { top: 10, right: 20, bottom: 30, left: 40 };
      // 18px padding each side on the card; fall back to 600 before the first measure.
      const cardInner = lineChartWidth ? lineChartWidth - 36 : 600;
      const width = Math.max(320, cardInner) - margin.left - margin.right;
      const height = 260 - margin.top - margin.bottom;

      const svg = d3.select(lineChartRef.current)
        .append("svg")
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom)
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);


      let xDomain;
      if (selectedMonth !== 'all' && selectedMonth) {
        // For a specific month, set domain to full month range
        const [year, month] = selectedMonth.split('-').map(Number);
        const startDate = new Date(year, month - 1, 1); // First day of month
        const endDate = new Date(year, month, 0); // Last day of month
        xDomain = [startDate, endDate];
      } else if (dailyData.length > 0) {
        // For 'all', use the extent of the data with padding for small datasets
        const extent = d3.extent(dailyData, d => d.date);

        // Add padding for better visualization when there are few data points
        if (dailyData.length === 1) {
          // For single point, add 30 days padding on each side
          const singleDate = extent[0];
          xDomain = [
            d3.timeDay.offset(singleDate, -30),
            d3.timeDay.offset(singleDate, 30)
          ];
        } else if (dailyData.length <= 5) {
          // For 2-5 points, add 20% padding based on the data range
          const timeDiff = extent[1] - extent[0];
          const padding = timeDiff * 0.2;
          xDomain = [
            new Date(extent[0].getTime() - padding),
            new Date(extent[1].getTime() + padding)
          ];
        } else {
          xDomain = extent;
        }
      } else {
        // No data, use a default range
        const now = new Date();
        xDomain = [new Date(now.getFullYear(), 0, 1), now];
      }

      const x = d3.scaleTime()
        .domain(xDomain)
        .range([10, width]);

      const y = d3.scaleLinear()
        .domain([0, dailyData.length > 0 ? d3.max(dailyData, d => d.count) * 1.2 : 10])
        .nice()
        .range([height, 0]);

      // Add vertical gridlines only
      const gridGenerator = d3.axisBottom(x)
        .tickSize(-height)
        .tickFormat("");

      // Match grid ticks with x-axis ticks
      if (selectedMonth !== 'all' && selectedMonth) {
        gridGenerator.ticks(d3.timeDay.every(1));
      } else if (selectedMonth === 'all') {
        gridGenerator.ticks(d3.timeMonth.every(1));
      }

      const gridLines = svg.append("g")
        .attr("class", "grid")
        .attr("transform", `translate(0,${height})`)
        .call(gridGenerator)
        .style("stroke-dasharray", ("4,8"))
        .style("stroke-width", "0.6px")
        .style("stroke", "#000000")
        .style("opacity", 0.2);

      gridLines.select(".domain").remove();

      // Add X axis
      const xAxisGenerator = d3.axisBottom(x)
        .tickFormat(xTickFormat)
        .tickSize(0)
        .tickPadding(10);

      // Show appropriate ticks based on selection
      if (selectedMonth !== 'all' && selectedMonth) {
        xAxisGenerator.ticks(d3.timeDay.every(1));
      } else if (selectedMonth === 'all') {
        xAxisGenerator.ticks(d3.timeMonth.every(1));
      }

      const xAxis = svg.append("g")
        .attr("transform", `translate(0,${height})`)
        .call(xAxisGenerator);

      xAxis.selectAll("text")
        .style("font-size", "12px")
        .style("fill", "#000000")
        .style("font-weight", "500");

      xAxis.select(".domain")
        .style("stroke", "none");

      // Add Y axis
      const yAxis = svg.append("g")
        .call(d3.axisLeft(y)
          .ticks(5)
          .tickSize(0)
          .tickPadding(10));

      yAxis.selectAll("text")
        .style("font-size", "13px")
        .style("fill", "#000000")
        .style("font-weight", "500");

      yAxis.select(".domain")
        .style("stroke", "none");

      // Add the line
      const line = d3.line()
        .x(d => x(d.date))
        .y(d => y(d.count))
        .curve(d3.curveMonotoneX);

      svg.append("path")
        .datum(dailyData)
        .attr("fill", "none")
        .attr("stroke", "#00C8FF")
        .attr("stroke-width", 2.5)
        .attr("d", line);

      // Add hover dots and tooltip
      const tooltip = d3.select("body").append("div")
        .attr("class", "d3-tooltip")
        .style("position", "absolute")
        .style("padding", "8px 12px")
        .style("background", "rgba(0, 0, 0, 0.8)")
        .style("color", "white")
        .style("border-radius", "4px")
        .style("font-size", "12px")
        .style("font-family", "Baikal Book")
        .style("pointer-events", "none")
        .style("opacity", 0);

      // Add circles for hover detection (visible for small datasets)
      const showVisibleDots = dailyData.length <= 5;
      svg.selectAll(".dot")
        .data(dailyData)
        .enter().append("circle")
        .attr("class", "dot")
        .attr("cx", d => x(d.date))
        .attr("cy", d => y(d.count))
        .attr("r", showVisibleDots ? 4 : 8)
        .attr("fill", showVisibleDots ? "#00C8FF" : "transparent")
        .attr("stroke", showVisibleDots ? "white" : "none")
        .attr("stroke-width", showVisibleDots ? 2 : 0)
        .style("cursor", "pointer")
        .on("mouseover", function(event, d) {
          // Show tooltip
          tooltip.transition()
            .duration(200)
            .style("opacity", 1);

          const dateStr = selectedMonth === 'all'
            ? d.date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
            : d.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

          tooltip.html(`<strong>${dateStr}</strong><br/>${d.count} transaction${d.count !== 1 ? 's' : ''}`)
            .style("left", (event.pageX + 10) + "px")
            .style("top", (event.pageY - 28) + "px");

          // Show larger dot on hover
          d3.select(this)
            .attr("r", 6)
            .attr("fill", "#00C8FF")
            .attr("stroke", "white")
            .attr("stroke-width", 2);
        })
        .on("mouseout", function() {
          tooltip.transition()
            .duration(500)
            .style("opacity", 0);

          // Return to original state
          d3.select(this)
            .attr("r", showVisibleDots ? 4 : 8)
            .attr("fill", showVisibleDots ? "#00C8FF" : "transparent")
            .attr("stroke", showVisibleDots ? "white" : "none")
            .attr("stroke-width", showVisibleDots ? 2 : 0);
        });
    };

    renderLineChart();
  }, [transactions, selectedMonth, lineChartWidth]);

  // Render Revenue by School Chart
  useEffect(() => {
    // Clear previous chart and tooltips
    d3.select(revenueChartRef.current).selectAll("*").remove();

    const renderRevenueChart = () => {
      // Calculate revenue by school for filtered transactions
      const revenueBySchool = d3.rollup(
        transactions,
        v => v.reduce((sum, t) => sum + getSchoolRevenue(t), 0),
        d => d.school?.id
      );

      // Convert to array and merge with school data
      const schoolRevenueData = Array.from(revenueBySchool, ([schoolId, revenue]) => {
        const school = schools.find(s => s.id === schoolId) ||
                       transactions.find(t => t.school?.id === schoolId)?.school;
        return {
          id: schoolId,
          name: school?.name || 'Unknown',
          image: school?.image || null,
          revenue: revenue
        };
      }).filter(d => d.id && d.revenue > 0)
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10); // Top 10 schools

      if (schoolRevenueData.length === 0) {
        d3.select(revenueChartRef.current)
          .append("div")
          .style("display", "flex")
          .style("align-items", "center")
          .style("justify-content", "center")
          .style("height", "260px")
          .style("width", "400px")
          .style("color", "#9CA3AF")
          .style("font-family", "Baikal Book")
          .style("font-size", "14px")
          .text("No revenue data for selected period");
        return;
      }

      // Get container width dynamically
      const containerWidth = revenueChartRef.current ? revenueChartRef.current.clientWidth : 600;
      const margin = { top: 20, right: 30, bottom: 70, left: 60 };
      const width = Math.max(containerWidth - margin.left - margin.right, 300);
      const height = 280 - margin.top - margin.bottom;

      const svg = d3.select(revenueChartRef.current)
        .append("svg")
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom)
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      // X scale (schools)
      const x = d3.scaleBand()
        .domain(schoolRevenueData.map(d => d.id))
        .range([0, width])
        .padding(0.4);

      // Y scale (revenue)
      const y = d3.scaleLinear()
        .domain([0, d3.max(schoolRevenueData, d => d.revenue) * 1.1])
        .nice()
        .range([height, 0]);

      // Add Y axis
      svg.append("g")
        .call(d3.axisLeft(y)
          .ticks(5)
          .tickFormat(d => {
            if (d === 0) return "$0";
            if (d < 1000) return `$${d.toFixed(0)}`;
            return `$${(d / 1000).toFixed(1)}k`;
          })
          .tickSize(0)
          .tickPadding(10))
        .selectAll("text")
        .style("font-size", "12px")
        .style("fill", "#000000")
        .style("font-weight", "500");

      svg.select(".domain")
        .style("stroke", "none");

      // Add bars container
      const bars = svg.selectAll(".bar")
        .data(schoolRevenueData)
        .enter().append("g")
        .attr("class", "bar-group");

      // Add invisible full-height hover area
      bars.append("rect")
        .attr("class", "hover-area")
        .attr("x", d => x(d.id))
        .attr("y", 0)  // Start from top
        .attr("width", x.bandwidth())
        .attr("height", height)  // Full height of chart
        .attr("fill", "transparent")
        .attr("pointer-events", "all");  // Ensure it captures mouse events

      // Add the base bar with background matching container
      bars.append("rect")
        .attr("class", "bar-base")
        .attr("x", d => x(d.id))
        .attr("y", d => y(d.revenue))
        .attr("width", x.bandwidth())
        .attr("height", d => height - y(d.revenue))
        .attr("fill", "#F8F8F8")  // Match container background
        .attr("pointer-events", "none");  // Don't interfere with hover area

      // Add individual horizontal lines with rounded ends
      bars.each(function(d) {
        const barHeight = height - y(d.revenue);
        const lineSpacing = 5;  // Space between lines
        const lineHeight = 2.5;  // Height of each line
        const numLines = Math.floor(barHeight / lineSpacing);
        const barX = x(d.id);
        const barWidth = x.bandwidth();
        const centerX = barX + barWidth / 2; // Calculate center once

        const barGroup = d3.select(this);

        // Add horizontal lines from bottom to top - centered
        for (let i = 0; i < numLines; i++) {
          const lineY = height - (i * lineSpacing + lineHeight);
          if (lineY > y(d.revenue)) {  // Only draw lines within the bar height
            const lineWidth = barWidth - 4;
            barGroup.append("rect")
              .attr("class", "horizontal-line")
              .attr("x", centerX - lineWidth / 2)  // Center the line
              .attr("y", lineY)
              .attr("width", lineWidth)
              .attr("height", lineHeight)
              .attr("fill", "#0000000F")  // Light black with opacity
              .attr("rx", 2)  // Subtle rounded corners
              .attr("ry", 2)
              .attr("pointer-events", "none");  // Don't interfere with hover area
          }
        }
      });

      // Add black rounded bar at the top - centered
      bars.append("rect")
        .attr("class", "bar-top")
        .attr("x", d => {
          const centerX = x(d.id) + x.bandwidth() / 2;
          const barWidth = x.bandwidth() - 4;
          return centerX - barWidth / 2;
        })
        .attr("y", d => y(d.revenue) - 1)  // Position at top of bar
        .attr("width", x.bandwidth() - 4)
        .attr("height", 2)  // Height of top bar
        .attr("fill", "#000")  // Black color
        .attr("rx", 2)  // Same rounded corners as horizontal lines
        .attr("ry", 2)
        .attr("pointer-events", "none");  // Don't interfere with hover area

      // Add school logos on X axis - centered under their columns with fixed dimensions
      const logoHeight = 24;  // Fixed height for all logos
      const logoWidth = 48;   // Fixed width for consistency
      schoolRevenueData.forEach((d, i) => {
        const barX = x(d.id);
        const barWidth = x.bandwidth();
        const centerX = barX + barWidth / 2;

        // Add logo with fixed dimensions, centered under the bar
        svg.append("image")
          .attr("class", "chart-school-logo")
          .attr("x", centerX - logoWidth / 2)  // Center the image
          .attr("y", height + 12)
          .attr("width", logoWidth)   // Fixed width
          .attr("height", logoHeight) // Fixed height
          .attr("href", d.image || '')
          .attr("preserveAspectRatio", "xMidYMid meet")  // Scale and center within box
          .attr("pointer-events", "none")
          .on("error", function() {
            // If logo fails to load, show text instead
            d3.select(this).remove();
            svg.append("text")
              .attr("x", centerX)
              .attr("y", height + 25)
              .attr("text-anchor", "middle")
              .style("font-size", "10px")
              .style("fill", "#666")
              .attr("pointer-events", "none")
              .text(d.name.substring(0, 3));
          });
      });


      // Add hover tooltip
      const tooltip = d3.select("body").append("div")
        .attr("class", "d3-tooltip")
        .style("position", "absolute")
        .style("padding", "8px 12px")
        .style("background", "rgba(0, 0, 0, 0.8)")
        .style("color", "white")
        .style("border-radius", "4px")
        .style("font-size", "12px")
        .style("font-family", "Baikal Book")
        .style("pointer-events", "none")
        .style("opacity", 0);

      // Add hover events to entire bar group
      bars
        .on("mouseover", function(event, d) {
          tooltip.transition()
            .duration(200)
            .style("opacity", 1);

          const formattedRevenue = d.revenue.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });

          tooltip.html(`<strong>${d.name}</strong><br/>Revenue: ${formattedRevenue}`)
            .style("left", (event.pageX + 10) + "px")
            .style("top", (event.pageY - 28) + "px");

          // Highlight the entire bar group
          d3.select(this).selectAll(".bar-base, .horizontal-line, .bar-top")
            .attr("opacity", 0.8);
        })
        .on("mouseout", function() {
          tooltip.transition()
            .duration(500)
            .style("opacity", 0);

          // Reset opacity for all elements in the group
          d3.select(this).selectAll(".bar-base, .horizontal-line, .bar-top")
            .attr("opacity", 1);
        })
        .style("cursor", "pointer");
    };

    renderRevenueChart();
  }, [transactions, schools]);

  const formatDate = (dateString) => {
    if (!dateString || dateString === 'Never' || dateString === 'N/A') {
      return dateString;
    }
    try {
      const date = new Date(dateString);
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const day = date.getDate().toString().padStart(2, '0');
      return `${month}, ${day}`;
    } catch (error) {
      return dateString;
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (error) {
      return '';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedTransactions = React.useMemo(() => {
    let filteredTransactions = [...transactions];

    // Apply search filter
    if (searchTerm) {
      filteredTransactions = filteredTransactions.filter(transaction => {
        const userFullName = transaction.user_name?.toLowerCase() || '';
        const userEmail = transaction.user_email?.toLowerCase() || '';
        const merchantName = transaction.merchant_name?.toLowerCase() || '';
        const schoolName = transaction.school?.name?.toLowerCase() || '';
        const search = searchTerm.toLowerCase();
        return userFullName.includes(search) ||
               userEmail.includes(search) ||
               merchantName.includes(search) ||
               schoolName.includes(search);
      });
    }

    // Sort filtered results
    if (!sortConfig.key) {
      return filteredTransactions;
    }

    return [...filteredTransactions].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'time':
        case 'date':
          aValue = new Date(a.created_at || a.auth_at || a.captured_at || 0);
          bValue = new Date(b.created_at || b.auth_at || b.captured_at || 0);
          break;
        case 'email':
          aValue = (a.user_email || '').toLowerCase();
          bValue = (b.user_email || '').toLowerCase();
          break;
        case 'name':
          aValue = (a.user_name || '').toLowerCase();
          bValue = (b.user_name || '').toLowerCase();
          break;
        case 'merchant':
          aValue = (a.merchant_name || a.description || '').toLowerCase();
          bValue = (b.merchant_name || b.description || '').toLowerCase();
          break;
        case 'school':
          aValue = (a.user?.active_school?.name || '').toLowerCase();
          bValue = (b.user?.active_school?.name || '').toLowerCase();
          break;
        case 'reward':
          aValue = getSchoolRevenue(a);
          bValue = getSchoolRevenue(b);
          break;
        case 'price':
          aValue = a.dollar_amount || 0;
          bValue = b.dollar_amount || 0;
          break;
        case 'hc_commission':
          aValue = getHcRevenue(a);
          bValue = getHcRevenue(b);
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'source':
          aValue = getTransactionSource(a).toLowerCase();
          bValue = getTransactionSource(b).toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [transactions, sortConfig, searchTerm]);

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <BackButton text="Back to Superuser Dashboard" to="/superuser/dashboard/" />
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: '#666'
            }}>
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton text="Back to Superuser Dashboard" to="/superuser/dashboard/" />
          <div style={{ marginBottom: '8px' }}>
            <div>
              <h1 className="staff-page-header">Manage Transactions</h1>
              <p className="staff-page-subheader">View and manage all transactions across the platform</p>
            </div>
            {/* Filters sit on their own row under the title rather than opposite
                it: there are four of them now, and a right-aligned cluster
                wrapped awkwardly against the heading. */}
            <div style={{
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
              alignItems: 'center',
              marginTop: '16px'
            }}>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                style={{ ...inlineSelectStyle('sm'), minWidth: '150px' }}
              >
                {generateMonthOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <select
                value={selectedSource}
                onChange={(e) => setSelectedSource(e.target.value)}
                style={{ ...inlineSelectStyle('sm'), minWidth: '130px' }}
              >
                {SOURCE_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>


              <SchoolPicker
                schools={schools}
                value={selectedSchool}
                onChange={(school) => {
                  setSelectedSchool(school);
                  setChoseAll(!school);
                }}
                allOptionLabel="All schools"
                allSelected={choseAll}
                size="sm"
              />

              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                style={inlineSelectStyle('sm')}
              >
                {FUNNEL_FILTER_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {!choseAll && !selectedSchool ? (
            <p className="staff-page-content" style={{ marginTop: '24px', opacity: 0.7 }}>
              Please select a school
            </p>
          ) : (
          <>

          {/* Stats Cards */}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginTop: '24px',
            flexWrap: 'wrap'
          }}>
            <div style={{
              flex: '1 1 300px',
              minWidth: '250px',
              maxWidth: '33%'
            }}>
              <StaffFansStatsCard
                title="Total Transactions"
                value={stats.loading ? "..." : stats.totalTransactions.toLocaleString()}
                iconName="card"
              />
            </div>
            <div style={{
              flex: '1 1 300px',
              minWidth: '250px',
              maxWidth: '33%'
            }}>
              <StaffFansStatsCard
                title="HC Revenue"
                value={stats.loading ? "..." : formatCurrency(stats.hcRevenue)}
                iconName="dollar-sign"
              />
            </div>
            <div style={{
              flex: '1 1 300px',
              minWidth: '250px',
              maxWidth: '33%'
            }}>
              <StaffFansStatsCard
                title="School Revenue"
                value={stats.loading ? "..." : formatCurrency(stats.schoolRevenue)}
                iconName="dollar-sign"
              />
            </div>
          </div>

          {/* D3 Charts Container */}
          <div style={{
            display: 'flex',
            gap: '24px',
            marginTop: '32px',
            marginBottom: '32px',
            flexWrap: 'wrap'
          }}>
            {/* Transaction Volume Chart */}
            <div
              ref={lineChartCardRef}
              style={{
                backgroundColor: '#F8F8F8',
                borderRadius: '16px',
                padding: '18px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                // ~12 points is where the labels start colliding at half width.
                flex: lineChartPointCount > 12 ? '1 1 100%' : '1 1 600px',
                minWidth: 0,
              }}
            >
              <div style={{
                marginBottom: '16px'
              }}>
                <h4 style={{
                  fontFamily: 'Baikal Book',
                  fontSize: '16px',
                  marginBottom: '4px',
                  fontWeight: '600',
                  color: '#111827',
                  margin: 0
                }}>{selectedMonth === 'all' ? 'Monthly Transactions' : 'Daily Transactions'}</h4>
                <p style={{
                  fontFamily: 'Baikal Book',
                  fontSize: '12px',
                  color: '#6B7280',
                  margin: 0
                }}>Transaction volume over time</p>
              </div>
              <div ref={lineChartRef}></div>
            </div>

            {/* School Revenue Chart */}
            <div style={{
              backgroundColor: '#F8F8F8',
              borderRadius: '16px',
              padding: '18px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
              flex: '1 1 100%',
              minWidth: '400px'
            }}>
              <div style={{
                marginBottom: '40px'
              }}>
                <h4 style={{
                  fontFamily: 'Baikal Book',
                  fontSize: '16px',
                  marginBottom: '4px',
                  fontWeight: '600',
                  color: '#111827',
                  margin: 0
                }}>{selectedMonth === 'all' ? 'Total School Revenue' : (() => {
                  const [year, month] = selectedMonth.split('-');
                  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                                      'July', 'August', 'September', 'October', 'November', 'December'];
                  return `${monthNames[parseInt(month) - 1]} ${year} Revenue`;
                })()}</h4>
                <p style={{
                  fontFamily: 'Baikal Book',
                  fontSize: '12px',
                  color: '#6B7280',
                  margin: 0
                }}>School earnings for selected period</p>
              </div>
              <div ref={revenueChartRef}></div>
            </div>
          </div>

          <h3 style={{
            fontFamily: 'Baikal Condensed',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: '900',
            lineHeight: 'normal',
            marginTop: '32px',
            marginBottom: '4px',
          }}>
            {selectedMonth === 'all' ? 'All Platform Transactions' : 'Transaction List'}
          </h3>

          <p style={{
            color: 'var(--Black, #000)',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: '340',
            lineHeight: 'normal',
            opacity: '0.5',
            marginTop: '0',
            marginBottom: '16px'
          }}>
            {selectedMonth === 'all' ? 'Comprehensive view of all transactions across all schools' : `Transactions for ${generateMonthOptions().find(opt => opt.value === selectedMonth)?.label || selectedMonth}`}
          </p>

          <div style={{
            display: 'flex',
            width: '400px',
            height: '44px',
            padding: '10px 14px',
            alignItems: 'center',
            gap: '8px',
            borderRadius: '12px',
            border: '0.5px solid rgba(0, 0, 0, 0.50)',
            marginBottom: '24px'
          }}>
            <div style={{
              opacity: '0.5',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Icon name="search" size={18} />
            </div>
            <input
              type="text"
              placeholder="Search by name, merchant, or school..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: '14px',
                background: 'transparent',
                color: 'var(--Black, #000)',
                fontFamily: '"Baikal Book"'
              }}
            />
          </div>

          {/* Table Container with horizontal scroll */}
          <div style={{
            overflowX: 'auto',
            width: '100%',
            maxWidth: '1500px'
          }}>
            {/* Table Header */}
            <div style={{
              display: 'flex',
              width: '100%',
              minWidth: '1300px',
              padding: '12px 18px',
              alignItems: 'center',
              borderRadius: '12px 12px 0 0',
              background: '#F8F8F8',
              backdropFilter: 'blur(22.5px)'
            }}>
            <div
              onClick={() => handleSort('time')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '7%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Time
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('email')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '15%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Email
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('name')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '12%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Name
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('school')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '6%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              School
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('merchant')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '9%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Merchant
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('hc_commission')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '8%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              To HC
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('reward')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '10%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              To School
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('price')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '10%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Spend
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('source')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '9%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Source
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('date')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '6%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Date
              <Icon name="up-down" size={12} />
            </div>
            <div
              onClick={() => handleSort('status')}
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '7%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                paddingRight: '12px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
              Status
              <Icon name="up-down" size={12} />
            </div>
            <div
              style={{
                fontFamily: '"Baikal Book"',
                fontWeight: '900',
                fontSize: '14px',
                lineHeight: '100%',
                letterSpacing: '0%',
                width: '3%',
                minWidth: '0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                paddingRight: '18px'
              }}>
            </div>
            </div>

            <div style={{ marginBottom: '8px' }}></div>

            {/* Table Rows */}
            {sortedTransactions.length === 0 ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px',
              background: '#F8F8F8',
              borderRadius: '12px',
              marginBottom: '8px'
            }}>
              <Icon name="card" size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
              <p style={{
                fontSize: '16px',
                fontFamily: '"Baikal Book"',
                color: '#666',
                margin: 0
              }}>
                No transactions found
              </p>
            </div>
          ) : (
            sortedTransactions.map((transaction, index) => (
              <div key={transaction.id || index} style={{
                display: 'flex',
                width: '100%',
                minWidth: '1300px',
                height: '59px',
                padding: '18px',
                alignItems: 'center',
                borderRadius: '12px',
                background: '#F8F8F8',
                marginBottom: '8px'
              }}>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '7%',
                  minWidth: '0',
                  flexShrink: 0,
                  opacity: '0.7',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: '59px',
                  paddingRight: '12px'
                }}>
                  {formatTime(transaction.created_at || transaction.auth_at || transaction.captured_at)}
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '15%',
                  minWidth: '0',
                  flexShrink: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: '59px',
                  paddingRight: '12px'
                }}>
                  {transaction.user_email || 'Unknown Email'}
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '12%',
                  minWidth: '0',
                  flexShrink: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: '59px',
                  paddingRight: '12px'
                }}>
                  {transaction.user_name || 'Unknown User'}
                </div>
                <div style={{
                  fontSize: '14px',
                  width: '6%',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  overflow: 'hidden',
                  paddingRight: '4px'
                }}>
                  {transaction.school?.image ? (
                    <img
                      src={transaction.school.image}
                      alt={transaction.school.name || 'School'}
                      style={{
                        width: 'auto',
                        height: '24px',
                        borderRadius: '4px',
                        objectFit: 'cover',
                        flexShrink: 0
                      }}
                    />
                  ) : (
                    <div style={{
                      width: 'auto',
                      height: '24px',
                      aspectRatio: '1',
                      borderRadius: '4px',
                      backgroundColor: '#e5e7eb',
                      flexShrink: 0
                    }} />
                  )}
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '9%',
                  minWidth: '0',
                  flexShrink: 0,
                  opacity: '0.7',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: '59px',
                  paddingRight: '12px'
                }}>
                  {transaction.merchant_name || '—'}
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '8%',
                  minWidth: '0',
                  flexShrink: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: '59px',
                  paddingRight: '12px',
                  fontWeight: '600',
                  color: '#2E7D32'
                }}>
                  ${getHcRevenue(transaction).toFixed(2)}
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '10%',
                  minWidth: '0',
                  flexShrink: 0,
                  fontWeight: '600',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: '59px',
                  paddingRight: '12px'
                }}>
                  {getSchoolRevenue(transaction) ? formatCurrency(getSchoolRevenue(transaction)) : '—'}
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '10%',
                  minWidth: '0',
                  flexShrink: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: '59px',
                  paddingRight: '12px',
                  fontWeight: '600'
                }}>
                  {transaction.dollar_amount ? formatCurrency(transaction.dollar_amount) : '—'}
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '9%',
                  minWidth: '0',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  overflow: 'hidden',
                  lineHeight: '59px',
                  paddingRight: '12px',
                  opacity: '0.7'
                }}>
                  {(() => {
                    const source = getTransactionSource(transaction);
                    const isWildfire = source === 'extension' || source === 'in-app';
                    const logoSrc = isWildfire ? wildfireLogo : oliveLogo;

                    // Only Olive and Wildfire have a logo here. Travel would
                    // otherwise fall through to the Olive one, since the branch
                    // below treats "not Wildfire" as "Olive".
                    if (!isWildfire && source !== 'in-person') {
                      return SOURCE_LABELS[source] || source;
                    }

                    return (
                      <>
                        <img
                          src={logoSrc}
                          alt={isWildfire ? 'Wildfire' : 'Olive'}
                          style={{
                            width: 'auto',
                            height: '16px',
                            objectFit: 'contain',
                            flexShrink: 0,
                            borderRadius: '4px'
                          }}
                        />
                        <span style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {source}
                        </span>
                      </>
                    );
                  })()}
                </div>
                <div style={{
                  fontFamily: '"Baikal Book"',
                  fontSize: '14px',
                  width: '6%',
                  minWidth: '0',
                  flexShrink: 0,
                  opacity: '0.7',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: '59px',
                  paddingRight: '12px'
                }}>
                  {formatDate(transaction.created_at || transaction.auth_at || transaction.captured_at)}
                </div>
                <div style={{
                  width: '7%',
                  minWidth: '0',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  overflow: 'hidden'
                }}>
                  <span style={{
                    fontFamily: '"Baikal Book"',
                    fontSize: '12px',
                    display: 'inline-flex',
                    padding: '4px 8px',
                    alignItems: 'center',
                    borderRadius: '100px',
                    background: (transaction.status === 'completed' || transaction.status === 'captured' || transaction.status === 'auth') ? '#D4F3D4' :
                              transaction.status === 'pending' ? '#FFF4CE' :
                              transaction.status === 'failed' ? '#FFE4E4' :
                              'rgba(0, 0, 0, 0.06)'
                  }}>
                    {(transaction.status === 'completed' || transaction.status === 'captured' || transaction.status === 'auth') ? 'complete' :
                     transaction.status === 'pending' ? 'pending' :
                     transaction.status === 'failed' ? 'failed' :
                     (transaction.status || 'unknown').toLowerCase()}
                  </span>
                </div>
                <div style={{
                  width: '3%',
                  minWidth: '0',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingRight: '18px'
                }}>
                  <button
                    onClick={() => {
                      const transactionId = transaction.id || `index-${index}`;
                      navigate(`/superuser/transaction/${transactionId}`);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s',
                      fontSize: '20px',
                      fontWeight: 'bold',
                      color: '#666',
                      lineHeight: '1'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                      e.currentTarget.style.color = '#000';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#666';
                    }}
                  >
                    ⋮
                  </button>
                </div>
              </div>
            ))
          )}
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuperuserTransactionManagement;